import React, { useState } from "react";
import {
    Table,
    TableBody,
    TableCell,
    TableHeader,
    TableRow,
} from "../ui/table";
import { baseURL } from "../../config/config";
import { isValidURL } from "../../utils/validation";
import { Edit, Trash2, Upload, Volume2 } from 'lucide-react';
import Waveform from "@/components/ui/sound/WareForm";
import { useDeleteFlashcard } from "@/hooks/useFlashcard";
import { useTranslation } from "react-i18next";
import FlashcardService from "@/services/flashcard.service";
import { toast } from "react-toastify";
import { useLocation, useNavigate } from "react-router";

interface deck {
    _id: string;
    title: string;
}

interface Flashcard {
    _id: string;
    title_FC: string;
    type: string;
    createdAt: string;
    deck: deck[];
    answer: string;
    image: string;
    sound: string;
}

interface FlashcardTableProps {
    flashcards: Flashcard[];
    currentPage: number;
    totalPages: number;
    onPageChange: (page: number) => void;
    isLoading: boolean;
    onImageUploaded?: () => void;
}

const LoadingSkeleton = () => (
    <div className="animate-pulse">
        <div className="space-y-4">
            {[...Array(5)].map((_, index) => (
                <div key={index} className="flex items-center space-x-4 py-3">
                    <div className="h-4 bg-gray-200 rounded w-1/4 dark:bg-gray-700"></div>
                    <div className="h-4 bg-gray-200 rounded w-1/4 dark:bg-gray-700"></div>
                    <div className="h-4 bg-gray-200 rounded w-1/6 dark:bg-gray-700"></div>
                    <div className="h-4 bg-gray-200 rounded w-1/6 dark:bg-gray-700"></div>
                    <div className="h-4 bg-gray-200 rounded w-1/6 dark:bg-gray-700"></div>
                </div>
            ))}
        </div>
    </div>
);

export default function FlashcardTable({ flashcards, currentPage, totalPages, onPageChange, isLoading, onImageUploaded }: FlashcardTableProps) {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [mediaType, setMediaType] = useState<"image" | "sound" | null>(null);
    const [mediaSrc, setMediaSrc] = useState<any>("");
    const [selectedFlashcard, setSelectedFlashcard] = useState<Flashcard | null>(null);
    const [uploadingImageFlashcardId, setUploadingImageFlashcardId] = useState<string>("");
    const [uploadingSoundFlashcardId, setUploadingSoundFlashcardId] = useState<string>("");
    const { mutate: deleteFlashcard, isLoading: isDeleting } = useDeleteFlashcard();
    const { t } = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();

    const handleOpenMedia = async (type: "image" | "sound", src: string) => {
        setMediaType(type);
        setMediaSrc(isValidURL(src) ? src : baseURL + src);
        setIsModalOpen(true);
    };

    const handleDelete = (id: string) => {
        deleteFlashcard(id, {
            onSuccess: () => {
                setSelectedFlashcard(null);
            },
        });
    };

    const handleUploadImage = async (flashcardId: string, file: File) => {
        const validImageTypes = ["image/jpeg", "image/png", "image/gif", "image/webp"];
        if (!validImageTypes.includes(file.type)) {
            toast.error(t("Please select a valid image file (JPEG, PNG, GIF, WEBP)"));
            return;
        }

        try {
            setUploadingImageFlashcardId(flashcardId);
            const fd = new FormData();
            fd.append("image", file);
            fd.append("_id", flashcardId);

            const updateResponse = await FlashcardService.editDeckFlashcardAdmin(fd);
            if (updateResponse?.data?.status === 200) {
                toast.success(t("Upload image successfully!"));
                onImageUploaded?.();
            } else {
                toast.error(t("Upload image failed!"));
            }
        } catch (error) {
            toast.error(t("Upload image failed!"));
            console.error("Error uploading flashcard image:", error);
        } finally {
            setUploadingImageFlashcardId("");
        }
    };

    const handleUploadSound = async (flashcardId: string, file: File) => {
        const validAudioTypes = ["audio/mpeg", "audio/mp3", "audio/wav", "audio/ogg", "audio/x-m4a", "audio/mp4"];
        if (!validAudioTypes.includes(file.type)) {
            toast.error(t("Please select a valid audio file (MP3, WAV, OGG, M4A)"));
            return;
        }

        try {
            setUploadingSoundFlashcardId(flashcardId);
            const fd = new FormData();
            fd.append("sound", file);
            fd.append("_id", flashcardId);

            const updateResponse = await FlashcardService.editDeckFlashcardAdmin(fd);
            if (updateResponse?.data?.status === 200) {
                toast.success(t("Upload sound successfully!"));
                onImageUploaded?.();
            } else {
                toast.error(t("Upload sound failed!"));
            }
        } catch (error) {
            toast.error(t("Upload sound failed!"));
            console.error("Error uploading flashcard sound:", error);
        } finally {
            setUploadingSoundFlashcardId("");
        }
    };

    const renderImageUpload = (flashcard: Flashcard, compact = false) => (
        <label
            className={`inline-flex items-center justify-center gap-1 cursor-pointer transition-all ${
                compact
                    ? "absolute bottom-1 right-1 w-7 h-7 rounded-full bg-white/90 dark:bg-gray-900/90 shadow-md hover:bg-indigo-500 hover:text-white text-gray-600 dark:text-gray-300 backdrop-blur-sm"
                    : "px-3 py-1.5 text-xs font-medium text-indigo-600 dark:text-indigo-400 border border-dashed border-indigo-300 dark:border-indigo-700 rounded-lg hover:bg-indigo-50 dark:hover:bg-indigo-500/10"
            }`}
            title={t("Upload image")}
        >
            {uploadingImageFlashcardId === flashcard._id ? (
                <svg className="w-3.5 h-3.5 animate-spin" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291..." />
                </svg>
            ) : (
                <Upload className="w-3.5 h-3.5" />
            )}
            {!compact && (uploadingImageFlashcardId === flashcard._id ? t("Uploading...") : t("Upload"))}
            <input
                type="file"
                accept="image/*"
                disabled={uploadingImageFlashcardId === flashcard._id}
                className="hidden"
                onChange={async (e) => {
                    const inputElement = e.currentTarget;
                    const file = e.target.files?.[0];
                    if (file) {
                        await handleUploadImage(flashcard._id, file);
                    }
                    if (inputElement) {
                        inputElement.value = "";
                    }
                }}
            />
        </label>
    );

    const renderSoundUpload = (flashcard: Flashcard, compact = false) => (
        <label
            className={`inline-flex items-center justify-center gap-1 cursor-pointer transition-all ${
                compact
                    ? "absolute bottom-1 right-1 w-7 h-7 rounded-full bg-white/90 dark:bg-gray-900/90 shadow-md hover:bg-indigo-500 hover:text-white text-gray-600 dark:text-gray-300 backdrop-blur-sm"
                    : "px-3 py-1.5 text-xs font-medium text-indigo-600 dark:text-indigo-400 border border-dashed border-indigo-300 dark:border-indigo-700 rounded-lg hover:bg-indigo-50 dark:hover:bg-indigo-500/10"
            }`}
            title={t("Upload sound")}
        >
            {uploadingSoundFlashcardId === flashcard._id ? (
                <svg className="w-3.5 h-3.5 animate-spin" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291..." />
                </svg>
            ) : (
                <Upload className="w-3.5 h-3.5" />
            )}
            {!compact && (uploadingSoundFlashcardId === flashcard._id ? t("Uploading...") : t("Upload"))}
            <input
                type="file"
                accept="audio/*"
                disabled={uploadingSoundFlashcardId === flashcard._id}
                className="hidden"
                onChange={async (e) => {
                    const inputElement = e.currentTarget;
                    const file = e.target.files?.[0];
                    if (file) {
                        await handleUploadSound(flashcard._id, file);
                    }
                    if (inputElement) {
                        inputElement.value = "";
                    }
                }}
            />
        </label>
    );

    return (
        <div className="overflow-hidden rounded-xl border border-gray-200 bg-white dark:border-white/[0.05] dark:bg-white/[0.03]">
            <div className="max-w-full overflow-x-auto">
                <div className="min-w-[1102px]">
                    <Table>
                        <TableHeader className="border-b border-gray-100 dark:border-white/[0.05]">
                            <TableRow>
                                <TableCell
                                    isHeader
                                    className="font-bold px-5 py-3 text-gray-700 dark:text-white/90 text-start"
                                >
                                    {t("Question")}
                                </TableCell>
                                <TableCell
                                    isHeader
                                    className="font-bold px-5 py-3 text-gray-700 dark:text-white/90 text-start"
                                >
                                    {t("Answer")}
                                </TableCell>
                                <TableCell
                                    isHeader
                                    className="font-bold px-5 py-3 text-gray-700 dark:text-white/90 text-start"
                                >
                                    {t("Image")}
                                </TableCell>
                                <TableCell
                                    isHeader
                                    className="font-bold px-5 py-3 text-gray-700 dark:text-white/90 text-start"
                                >
                                    {t("Sound")}
                                </TableCell>
                                <TableCell
                                    isHeader
                                    className="font-bold px-5 py-3 text-gray-700 dark:text-white/90 text-start"
                                >
                                    {t("Actions")}
                                </TableCell>
                            </TableRow>
                        </TableHeader>

                        <TableBody className="divide-y divide-gray-100 dark:divide-white/[0.05]">
                            {isLoading ? (
                                <LoadingSkeleton />
                            ) : flashcards.map((flashcard) => (
                                <TableRow key={flashcard._id}>
                                    <TableCell className="px-5 py-4 sm:px-6 text-start w-1/3 dark:text-gray-400">
                                        {flashcard.title_FC}
                                    </TableCell>
                                    <TableCell className="px-4 py-3 text-gray-500 text-start text-theme-sm dark:text-gray-400 w-1/3">
                                        {flashcard.answer}
                                    </TableCell>
                                    <TableCell className="px-4 py-3 text-gray-500 text-start text-theme-sm dark:text-gray-400">
                                        <div className="relative group w-20 h-20">
                                            {flashcard.image ? (
                                                <>
                                                    <button
                                                        onClick={() => handleOpenMedia("image", flashcard.image)}
                                                        className="w-20 h-20 rounded-lg overflow-hidden border border-gray-200 dark:border-white/10 shadow-sm hover:shadow-md hover:ring-2 hover:ring-indigo-400 transition-all duration-200"
                                                    >
                                                        <img
                                                            src={flashcard.image}
                                                            alt="Preview"
                                                            className="w-full h-full object-cover transition-transform duration-200 group-hover:scale-105"
                                                        />
                                                    </button>
                                                    {/* nút upload nhỏ xuất hiện đè lên góc ảnh khi hover */}
                                                    <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                                                        {renderImageUpload(flashcard, true)}
                                                    </div>
                                                </>
                                            ) : (
                                                <label className="flex flex-col items-center justify-center w-20 h-20 rounded-lg border-2 border-dashed border-gray-300 dark:border-gray-700 text-gray-400 hover:border-indigo-400 hover:text-indigo-500 hover:bg-indigo-50/50 dark:hover:bg-indigo-500/5 cursor-pointer transition-all">
                                                    {uploadingImageFlashcardId === flashcard._id ? (
                                                        <svg className="w-5 h-5 animate-spin" viewBox="0 0 24 24">
                                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291..." />
                                                        </svg>
                                                    ) : (
                                                        <>
                                                            <Upload className="w-4 h-4 mb-1" />
                                                            <span className="text-[10px]">{t("Upload")}</span>
                                                        </>
                                                    )}
                                                    <input
                                                        type="file"
                                                        accept="image/*"
                                                        disabled={uploadingImageFlashcardId === flashcard._id}
                                                        className="hidden"
                                                        onChange={async (e) => {
                                                            const inputElement = e.currentTarget;
                                                            const file = e.target.files?.[0];
                                                            if (file) {
                                                                await handleUploadImage(flashcard._id, file);
                                                            }
                                                            if (inputElement) {
                                                                inputElement.value = "";
                                                            }
                                                        }}
                                                    />
                                                </label>
                                            )}
                                        </div>
                                    </TableCell>
                                    <TableCell className="px-4 py-3 text-gray-500 text-start text-theme-sm dark:text-gray-400">
                                        <div className="relative group w-20 h-20">
                                            {flashcard.sound ? (
                                                <>
                                                    <button
                                                        onClick={() => handleOpenMedia("sound", flashcard.sound)}
                                                        className="flex flex-col items-center justify-center w-20 h-20 rounded-lg border border-gray-200 dark:border-white/10 shadow-sm hover:shadow-md hover:ring-2 hover:ring-indigo-400 transition-all duration-200 bg-gradient-to-br from-indigo-50 to-purple-50 dark:from-indigo-500/10 dark:to-purple-500/10"
                                                    >
                                                        <Volume2 className="w-6 h-6 text-indigo-500 dark:text-indigo-400 transition-transform duration-200 group-hover:scale-110" />
                                                        <span className="text-[10px] mt-1 text-indigo-600 dark:text-indigo-400 font-medium">
                                                            {t("Play")}
                                                        </span>
                                                    </button>
                                                    {/* nút upload nhỏ đè lên góc khi hover, giống ảnh */}
                                                    <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                                                        {renderSoundUpload(flashcard, true)}
                                                    </div>
                                                </>
                                            ) : (
                                                <label className="flex flex-col items-center justify-center w-20 h-20 rounded-lg border-2 border-dashed border-gray-300 dark:border-gray-700 text-gray-400 hover:border-indigo-400 hover:text-indigo-500 hover:bg-indigo-50/50 dark:hover:bg-indigo-500/5 cursor-pointer transition-all">
                                                    {uploadingSoundFlashcardId === flashcard._id ? (
                                                        <svg className="w-5 h-5 animate-spin" viewBox="0 0 24 24">
                                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291..." />
                                                        </svg>
                                                    ) : (
                                                        <>
                                                            <Upload className="w-4 h-4 mb-1" />
                                                            <span className="text-[10px]">{t("Upload")}</span>
                                                        </>
                                                    )}
                                                    <input
                                                        type="file"
                                                        accept="audio/*"
                                                        disabled={uploadingSoundFlashcardId === flashcard._id}
                                                        className="hidden"
                                                        onChange={async (e) => {
                                                            const inputElement = e.currentTarget;
                                                            const file = e.target.files?.[0];
                                                            if (file) {
                                                                await handleUploadSound(flashcard._id, file);
                                                            }
                                                            if (inputElement) {
                                                                inputElement.value = "";
                                                            }
                                                        }}
                                                    />
                                                </label>
                                            )}
                                        </div>
                                    </TableCell>
                                    <TableCell className="font-bold px-4 py-3 text-gray-500 text-start text-theme-sm dark:text-gray-400 w-1/9">
                                        <button
                                            onClick={() => {
                                                navigate(`/edit-flashcard/${flashcard._id}`, {
                                                    state: {
                                                        returnTo: `${location.pathname}${location.search}`
                                                    }
                                                });
                                            }}
                                            className="p-2 text-blue-500 hover:bg-blue-100 rounded-md"
                                        >
                                            <Edit className="w-5 h-5" />
                                        </button>
                                        <button
                                            onClick={() => setSelectedFlashcard(flashcard)}
                                            className="p-2 text-red-500 hover:bg-red-100 rounded-md"
                                        >
                                            <Trash2 className="w-5 h-5" />
                                        </button>
                                        {selectedFlashcard && (
                                            <>
                                                <div className="fixed inset-0 z-40 flex items-center justify-center bg-black bg-opacity-20">
                                                    <div className="bg-white rounded-lg shadow-lg w-full max-w-lg p-6 relative">
                                                        <h2 className="text-xl font-semibold mb-4">  {t("Confirm Deletion")}</h2>
                                                        <p className="text-gray-600 mb-6">
                                                            {t("Are you sure you want to delete flashcard")} <b>{selectedFlashcard.title_FC}</b>?{t("This action is irreversible.")}
                                                        </p>
                                                        <div className="flex justify-end gap-2">
                                                            <button
                                                                onClick={() => setSelectedFlashcard(null)}
                                                                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                                                            >
                                                                {t("Cancel")}
                                                            </button>
                                                            <button
                                                                onClick={() => handleDelete(selectedFlashcard._id)}
                                                                className="px-4 py-2 text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors"
                                                            >
                                                                {isDeleting ? (
                                                                    <div className="flex items-center gap-2">
                                                                        <svg className="w-5 h-5 animate-spin" viewBox="0 0 24 24">
                                                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                                                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291..." />
                                                                        </svg>
                                                                        {t("Deleting")}...
                                                                    </div>
                                                                ) : (
                                                                    <>{t("Delete")}</>
                                                                )}
                                                            </button>
                                                        </div>
                                                    </div>
                                                </div>
                                            </>
                                        )}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </div>
            </div>

            {flashcards.length !== 0 && (
                <div className="flex items-center justify-between px-4 py-3 bg-white border-t border-gray-200 dark:bg-white/[0.03] dark:border-white/[0.05]">
                    <div className="flex flex-1 justify-center">
                        <nav className="relative z-0 inline-flex -space-x-px rounded-md" aria-label="Pagination">
                            <button
                                onClick={() => onPageChange(currentPage - 1)}
                                disabled={currentPage === 1}
                                className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-sm font-medium text-gray-500 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                <span className="sr-only">Previous</span>
                                <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                                    <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
                                </svg>
                            </button>

                            {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                                <button
                                    key={page}
                                    onClick={() => onPageChange(page)}
                                    className={`relative inline-flex items-center px-4 py-2 text-sm font-medium ${currentPage === page
                                        ? "z-10 bg-indigo-600 text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
                                        : "text-gray-500 bg-white border border-gray-300 hover:bg-gray-50"
                                        }`}
                                >
                                    {page}
                                </button>
                            ))}

                            <button
                                onClick={() => onPageChange(currentPage + 1)}
                                disabled={currentPage === totalPages}
                                className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-sm font-medium text-gray-500 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                <span className="sr-only">Next</span>
                                <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                                    <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                                </svg>
                            </button>
                        </nav>
                    </div>
                </div>
            )}

            {isModalOpen && (
                <>
                    <div className="fixed inset-0 z-40 flex items-center justify-center bg-black bg-opacity-50">
                        <div className="bg-white rounded-lg shadow-lg w-full max-w-lg p-6 relative">
                            <button
                                onClick={() => setIsModalOpen(false)}
                                className="absolute top-3 right-3 text-gray-500 hover:text-gray-800 text-2xl"
                            >
                                &times;
                            </button>

                            <h2 className="text-lg font-semibold mb-4 text-center">
                                {mediaType === "image"
                                    ? t("Image Preview")
                                    : t("Sound Preview")}
                            </h2>

                            <div className="text-center">
                                {mediaType === "image" ? (
                                    <img src={mediaSrc} alt="Preview" className="max-h-96 mx-auto rounded-md" />
                                ) : (
                                    <div className="mx-auto w-full">
                                        <Waveform url={mediaSrc} />
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
}
